import {
  derivePipelineStatusFromShowing,
  type LeadPipelineStatus,
  type ShowingRequest,
  type ShowingStatus,
} from '@/lib/types/showing';

const STORAGE_KEY = 'strashin:showings:v1';
const LEGACY_APPOINTMENTS_KEY = 'strashin:public:appointments:v1';

export function loadShowings(): ShowingRequest[] {
  if (typeof window === 'undefined') return [];

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (typeof stored === 'string' && stored.length > 0) {
      const parsed: unknown = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return parsed.filter(isShowingRequest).map(normalizeShowingRequest).sort(sortByCreatedAt);
      }
    }

    const migrated = migrateLegacyAppointments();
    if (migrated.length > 0) {
      persistShowings(migrated);
      return migrated.sort(sortByCreatedAt);
    }
  } catch {
    // ignore parse failures
  }

  return [];
}

export function saveShowingRequest(request: ShowingRequest): void {
  if (typeof window === 'undefined') return;
  const current = loadShowings();
  const normalized = normalizeShowingRequest(request);
  const next = [...current.filter((entry) => entry.id !== normalized.id), normalized];
  persistShowings(next);
}

export function updateShowingRequest(
  id: string,
  patch: Partial<ShowingRequest>,
): ShowingRequest | null {
  if (typeof window === 'undefined') return null;

  const current = loadShowings();

  const next = current.map((entry) => {
    if (entry.id !== id) return entry;
    return normalizeShowingRequest({
      ...entry,
      ...patch,
      updatedAt: patch.updatedAt ?? new Date().toISOString(),
    });
  });

  const updated = next.find((entry) => entry.id === id) ?? null;

  if (updated === null) {
    return null;
  }

  persistShowings(next);
  return updated;
}

export function upsertShowingStatus(
  id: string,
  status: ShowingStatus,
  pipelineStatus?: LeadPipelineStatus,
): ShowingRequest | null {
  const patch: Partial<ShowingRequest> = {
    status,
  };

  if (pipelineStatus !== undefined) {
    patch.pipelineStatus = pipelineStatus;
  }

  return updateShowingRequest(id, patch);
}

function persistShowings(showings: ShowingRequest[]): void {
  try {
    const sorted = [...showings].sort(sortByCreatedAt);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sorted));
  } catch {
    // ignore persistence errors
  }
}

function migrateLegacyAppointments(): ShowingRequest[] {
  if (typeof window === 'undefined') return [];

  try {
    const raw = window.localStorage.getItem(LEGACY_APPOINTMENTS_KEY);
    if (raw === null || raw.length === 0) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .map((entry) => convertLegacyAppointment(entry))
      .filter((value): value is ShowingRequest => value !== null)
      .map(normalizeShowingRequest);
  } catch {
    return [];
  }
}

function convertLegacyAppointment(value: unknown): ShowingRequest | null {
  if (typeof value !== 'object' || value === null) return null;
  const candidate = value as Partial<{
    id: string;
    propertyId: string;
    propertyCode: string;
    unitId: string;
    unitCode: string;
    visitorEmail: string;
    date: string;
    time: string;
    notes: string | null;
    createdAt: string;
  }>;

  if (
    typeof candidate.id !== 'string' ||
    typeof candidate.propertyId !== 'string' ||
    typeof candidate.unitId !== 'string' ||
    typeof candidate.visitorEmail !== 'string'
  ) {
    return null;
  }

  const createdAt =
    typeof candidate.createdAt === 'string' ? candidate.createdAt : new Date().toISOString();

  return {
    id: candidate.id,
    leadId: null,
    propertyId: candidate.propertyId,
    propertyCode: typeof candidate.propertyCode === 'string' ? candidate.propertyCode : 'UNKNOWN',
    unitId: candidate.unitId,
    unitLabel: typeof candidate.unitCode === 'string' ? candidate.unitCode : 'UNKNOWN',
    visitorName: candidate.visitorEmail,
    visitorEmail: candidate.visitorEmail,
    visitorPhone: '',
    preferredDate: typeof candidate.date === 'string' ? candidate.date : '',
    preferredTime: typeof candidate.time === 'string' ? candidate.time : '',
    message:
      typeof candidate.notes === 'string' && candidate.notes.length > 0 ? candidate.notes : null,
    status: 'confirmed',
    pipelineStatus: 'showing_scheduled',
    createdAt,
    updatedAt: createdAt,
  };
}

function normalizeShowingRequest(value: ShowingRequest): ShowingRequest {
  const status = normalizeStatus(value.status);
  const pipelineStatus = derivePipelineStatusFromShowing(status, value.pipelineStatus);
  return {
    ...value,
    unitLabel: value.unitLabel.trim(),
    status,
    message:
      typeof value.message === 'string' && value.message.trim().length > 0 ? value.message : null,
    visitorName: value.visitorName.trim(),
    visitorEmail: value.visitorEmail.trim().toLowerCase(),
    visitorPhone: value.visitorPhone.trim(),
    pipelineStatus,
  };
}

function isShowingRequest(value: unknown): value is ShowingRequest {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<ShowingRequest>;

  return (
    typeof candidate.id === 'string' &&
    typeof candidate.propertyId === 'string' &&
    typeof candidate.propertyCode === 'string' &&
    typeof candidate.unitId === 'string' &&
    typeof candidate.unitLabel === 'string' &&
    typeof candidate.visitorName === 'string' &&
    typeof candidate.visitorEmail === 'string' &&
    typeof candidate.visitorPhone === 'string' &&
    typeof candidate.preferredDate === 'string' &&
    typeof candidate.preferredTime === 'string' &&
    typeof candidate.status === 'string' &&
    typeof candidate.pipelineStatus === 'string' &&
    typeof candidate.createdAt === 'string' &&
    typeof candidate.updatedAt === 'string'
  );
}

function sortByCreatedAt(a: ShowingRequest, b: ShowingRequest): number {
  return a.createdAt.localeCompare(b.createdAt);
}

function normalizeStatus(status: unknown): ShowingStatus {
  if (status === 'rescheduled') return 'rescheduled';
  if (status === 'cancelled') return 'cancelled';
  return 'confirmed';
}
